import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Mail, Hash, Loader2, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

export default function FindBooking() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !code.trim()) return;
    setBusy(true);
    setResults(null);
    try {
      const { data } = await api.post("/public/bookings/lookup", {
        email: email.trim(),
        code: code.trim(),
      });
      setResults(data);
      if (data.length === 1) {
        // Fast-path: single match → jump straight to the confirmation page
        setTimeout(() => navigate(data[0].share_url), 400);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not find that booking");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="find-booking-page" className="mx-auto max-w-lg px-5 sm:px-8 py-16">
      <div className="glass rounded-3xl p-6 sm:p-8 space-y-5">
        <div className="text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-electric/15 flex items-center justify-center mb-3">
            <Search size={22} className="text-electric" />
          </div>
          <div className="label-caps text-electric text-xs">Self-serve lookup</div>
          <h1 className="font-serif text-3xl sm:text-4xl mt-1">Find my booking</h1>
          <p className="text-white/60 text-sm mt-2">
            Lost your confirmation link? Enter the email you booked with plus your
            confirmation code (or its last 4-8 characters).
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <div className="label-caps text-white/60 mb-2 flex items-center gap-1.5">
              <Mail size={12} className="text-electric" />Email used to book
            </div>
            <input
              data-testid="find-email"
              type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="input"
              autoComplete="email" required
            />
          </label>
          <label className="block">
            <div className="label-caps text-white/60 mb-2 flex items-center gap-1.5">
              <Hash size={12} className="text-electric" />Confirmation code
            </div>
            <input
              data-testid="find-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. bk_c2a7734f3ead   or just  734f3ead"
              className="input font-mono"
              required minLength={4}
            />
            <div className="text-[11px] text-white/40 mt-1.5">
              The full code appears on the confirmation screen; the last 4-8 characters also work.
            </div>
          </label>

          <button
            data-testid="find-submit"
            type="submit"
            disabled={busy}
            className="btn-gold w-full py-3 rounded-full text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            {busy ? "Searching…" : "Find my booking"}
          </button>
        </form>

        {results && results.length === 0 && (
          <div className="rounded-xl border border-white/10 bg-white/[.03] p-4 text-sm text-white/60 text-center">
            No booking matched those details.
          </div>
        )}

        {results && results.length > 0 && (
          <div className="rounded-xl border border-electric/25 bg-electric/5 p-4 space-y-3">
            <div className="label-caps text-electric text-[10px]">
              {results.length === 1 ? "Found it — redirecting…" : `${results.length} matches`}
            </div>
            {results.map((r) => (
              <Link
                key={r.id}
                to={r.share_url}
                data-testid={`find-result-${r.id}`}
                className="block rounded-lg bg-ink-950/60 border border-white/8 p-3 hover:border-gold text-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-serif text-base">{r.vehicle_name}</div>
                    <div className="text-xs text-white/50">
                      {r.type} · {r.status} · ${r.amount?.toFixed(2)}
                    </div>
                    <div className="text-[11px] text-white/40 font-mono mt-1 truncate">
                      {r.id}
                    </div>
                  </div>
                  <ExternalLink size={14} className="text-electric shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <p className="text-center text-[11px] text-white/40 mt-6">
        Still stuck? Tap the <span className="text-white">Concierge · 24/7</span> chat in the corner —
        we'll dig it up for you.
      </p>

      <style>{`
        .input {
          background: #121214; border: 1px solid rgba(255,255,255,0.08);
          color: #fff; border-radius: 12px; padding: 12px 14px;
          width: 100%; font-size: 14px; transition: border-color 0.2s;
        }
        .input:focus { outline: none; border-color: #F9C97C; box-shadow: 0 0 0 3px rgba(249,201,124,0.15); }
      `}</style>
    </section>
  );
}
